/*
 * @Copyright 2026. Institute for Future Intelligence, Inc.
 */

import { useStore } from '../stores/common';
import { usePrimitiveStore } from '../stores/commonPrimitive';
import { Undoable } from './Undoable';

// Every mutation routed through these helpers changes persisted projectState, so
// it must mark the project dirty. Centralized here (rather than relying on ~46
// call sites to each remember setChanged) so no mutating tool / gizmo / popover
// can silently leave the project looking saved. setChanged() no-ops in view-only
// mode, and these helpers are only ever invoked from user/agent actions — never
// during project load, hydration, or migration — so this can't false-dirty.
const markDirty = (): void => usePrimitiveStore.getState().setChanged(true);

/**
 * Forget the undo history — call whenever a DIFFERENT project replaces the
 * current one (open, example, new, template, the open project deleted). The
 * history is app-wide and outlives a projectState swap, and its entries act
 * on whatever project is current: crystals and molecules are found by name, so
 * Ctrl+Z after a switch deleted the new project's same-named crystal, or put
 * the old project's deleted crystal/molecule into it (and a save kept that).
 */
export const clearUndoHistory = (): void => useStore.getState().undoManager.clear();

/**
 * Apply a change with an undo entry, in one call.
 *
 * Replaces the manual pattern:
 *
 *   const undoable = {
 *     name: 'Change X',
 *     timestamp: Date.now(),
 *     oldValue, newValue,
 *     undo: () => apply(oldValue),
 *     redo: () => apply(newValue),
 *   } as UndoableChange;
 *   addUndoable(undoable);
 *   apply(newValue);
 *
 * with:
 *
 *   setWithUndo('Change X', oldValue, newValue, apply);
 *
 * Use this for any state mutation a user would expect Ctrl+Z to reverse.
 * Mutations that bypass it won't be in the undo stack — that's the bug
 * we're trying to stop creating.
 */
export const setWithUndo = <T>(name: string, oldValue: T, newValue: T, apply: (v: T) => void): void => {
  apply(newValue);
  const undoable: Undoable = {
    name,
    timestamp: Date.now(),
    undo: () => {
      apply(oldValue);
      markDirty();
    },
    redo: () => {
      apply(newValue);
      markDirty();
    },
  };
  useStore.getState().addUndoable(undoable);
  markDirty();
};

/**
 * Lower-level HOF for cases that don't fit the value-pair shape (multi-field
 * changes, structural mutations, etc.). Registers the undoable then runs
 * `redo()` once to apply.
 */
export const runWithUndo = (
  name: string,
  undo: () => void,
  redo: () => void,
  opts: { blocked?: () => boolean } = {},
): void => {
  redo();
  useStore.getState().addUndoable({
    name,
    timestamp: Date.now(),
    blocked: opts.blocked,
    undo: () => {
      undo();
      markDirty();
    },
    redo: () => {
      redo();
      markDirty();
    },
  });
  markDirty();
};
